import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import Modal from "react-bootstrap/Modal";
import styles from "../../_css/NotificationManagement.module.css";
import { getCategories, updateCatgoriesVisibility } from "../../requests";
import { notifyInfo, notifySuccess, notifyError } from "../helper/Toaster";

const CategoryVisibilityModal = ({ show, onHide, onCategoryUpdate }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    // Helper to format language code
    const formatLang = (lang) => {
        if (!lang) return "";
        return lang.toLowerCase() === "english" ? "en" :
            lang.toLowerCase() === "dutch" ? "nl" : lang.substring(0, 2).toLowerCase();
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const res = await getCategories();
            if (Array.isArray(res.categories)) {
                setCategories(res.categories);
            } else {
                notifyInfo("No categories found");
            }
        } catch (err) {
            notifyError("Failed to fetch categories");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            fetchCategories();
            setHasChanges(false);
        }
    }, [show]);

    const handleToggle = async (category) => {
        if (savingId === category.value) return;
        try {
            setSavingId(category.value);
            const res = await updateCatgoriesVisibility({
                category_id: category.value,
            });

            if (res?.success === true) {
                setCategories((prev) =>
                    prev.map((c) =>
                        c.value === category.value
                            ? { ...c, isVisible: res.isVisible }
                            : c
                    )
                );
                setHasChanges(true);
                notifySuccess(res.message);
            } else {
                notifyError(res?.message || "Update failed");
            }
        } catch (err) {
            console.error(err);
            notifyError("Something went wrong");
        } finally {
            setSavingId(null);
        }
    };

    const handleClose = () => {
        if (hasChanges && onCategoryUpdate) {
            onCategoryUpdate();
        }
        setCategories([]);
        onHide();
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            dialogClassName={styles.modal38}
        >
            <Modal.Header className={styles.modalHeader}>
                <Modal.Title className={styles.modalTitle}>
                    Manage Category Visibility
                </Modal.Title>
                <button onClick={handleClose} className={styles.closeButton}>
                    <X size={24} color="#374151" />
                </button>
            </Modal.Header>

            <Modal.Body className={styles.modalBody}>
                {loading ? (
                    <div className="text-center py-3">Loading categories...</div>
                ) : (
                    /* Set max-height for roughly 6 items (approx 60px per row) and enable overflow */
                    <div
                        className={styles.listContainer}
                        style={{
                            maxHeight: "380px",
                            overflowY: "auto",
                            paddingRight: "8px"
                        }}
                    >
                        {categories.length === 0 ? (
                            <p className="text-center py-3">No categories available</p>
                        ) : (
                            categories.map((category) => (
                                <div key={category._id || category.value} className={styles.categoryRow}>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className={styles.categoryLabel}>
                                            {category.label}
                                        </span>
                                        {category.language && (
                                            <span style={{
                                                fontSize: '11px',
                                                color: '#00B3B3',
                                                background: '#E6F8F9',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                fontWeight: '600'
                                            }}>
                                                ({formatLang(category.language)})
                                            </span>
                                        )}
                                    </div>

                                    <div className={styles.toggleWrapper}>
                                        <span
                                            className={
                                                category.isVisible
                                                    ? styles.statusActive
                                                    : styles.statusInactive
                                            }
                                        >
                                            {category.isVisible ? "Active" : "Not Active"}
                                        </span>

                                        <label className={styles.switch}>
                                            <input
                                                type="checkbox"
                                                checked={category.isVisible}
                                                disabled={savingId === category.value}
                                                onChange={() => handleToggle(category)}
                                            />
                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                        </label>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                <div className={styles.modalFooter}>
                    <button onClick={handleClose} className={styles.backButton}>
                        Close
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default CategoryVisibilityModal;